/** Perspective straighten for invoice photos (4 corner anchors → flat JPEG). */

export function isInvoiceImageFile(file) {
  if (!file) return false;
  const type = (file.type || "").toLowerCase();
  // Empty type is common for Mac Photos / Airdrop / some Finder picks — trust extension.
  if (type.startsWith("image/")) return true;
  return /\.(jpe?g|png|webp|heic|heif|gif|bmp|tif{1,2})$/i.test(file.name || "");
}

export function isInvoicePdfFile(file) {
  if (!file) return false;
  const type = (file.type || "").toLowerCase();
  return type === "application/pdf" || /\.pdf$/i.test(file.name || "");
}

export function defaultCorners(width, height) {
  const insetX = Math.max(8, width * 0.06);
  const insetY = Math.max(8, height * 0.06);
  return [
    { x: insetX, y: insetY },
    { x: width - insetX, y: insetY },
    { x: width - insetX, y: height - insetY },
    { x: insetX, y: height - insetY },
  ];
}

function dist(a, b) {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

/** Solve 8×8 linear system (Gaussian elimination). Returns null on failure. */
function solveLinear8(A, b) {
  const n = 8;
  const M = A.map((row, i) => [...row, b[i]]);
  for (let col = 0; col < n; col += 1) {
    let pivot = col;
    for (let r = col + 1; r < n; r += 1) {
      if (Math.abs(M[r][col]) > Math.abs(M[pivot][col])) pivot = r;
    }
    if (Math.abs(M[pivot][col]) < 1e-10) return null;
    if (pivot !== col) {
      const tmp = M[col];
      M[col] = M[pivot];
      M[pivot] = tmp;
    }
    const div = M[col][col];
    for (let c = col; c <= n; c += 1) M[col][c] /= div;
    for (let r = 0; r < n; r += 1) {
      if (r === col) continue;
      const f = M[r][col];
      if (f === 0) continue;
      for (let c = col; c <= n; c += 1) M[r][c] -= f * M[col][c];
    }
  }
  return M.map((row) => row[n]);
}

/**
 * Homography H mapping destination → source (for sampling).
 * Corners order: TL, TR, BR, BL. Each {x,y}.
 * Returns flat [h11..h33] with h33 = 1, or null.
 */
export function destToSrcHomography(srcCorners, dstCorners) {
  const A = [];
  const b = [];
  for (let i = 0; i < 4; i += 1) {
    const xs = srcCorners[i].x;
    const ys = srcCorners[i].y;
    const xd = dstCorners[i].x;
    const yd = dstCorners[i].y;
    A.push([xd, yd, 1, 0, 0, 0, -xs * xd, -xs * yd]);
    b.push(xs);
    A.push([0, 0, 0, xd, yd, 1, -ys * xd, -ys * yd]);
    b.push(ys);
  }
  const h = solveLinear8(A, b);
  if (!h) return null;
  return [...h, 1];
}

function applyH(H, x, y) {
  const w = H[6] * x + H[7] * y + H[8];
  if (Math.abs(w) < 1e-10) return { x: 0, y: 0 };
  return {
    x: (H[0] * x + H[1] * y + H[2]) / w,
    y: (H[3] * x + H[4] * y + H[5]) / w,
  };
}

/*
 * [Fable 5.1 · Warp output quality] — agreed with the product owner 2026-10-27
 * after straightened invoices came out visibly softer than the camera frame.
 *
 * DESIGN INVARIANTS
 *   1. No downscale on the way through the warp: the output rectangle is the
 *      sheet's own pixel size (longest edges), capped only at
 *      WARP_OUTPUT_MAX_SIDE for memory. Never 2000 again.
 *   2. Resampling is bicubic (Catmull-Rom), not bilinear. Bilinear at
 *      sub-pixel offsets blurs thin strokes and table rules.
 *   3. JPEG re-encode at WARP_JPEG_QUALITY (0.95) everywhere a file leaves the
 *      straighten dialog (warp, rotate, enhance toggle). Do not lower it to
 *      save bytes — Bedrock input and the stored invoice are the same file.
 *   4. Border pixels outside the source map to white, same as before.
 */
export const WARP_OUTPUT_MAX_SIDE = 3200;
export const WARP_JPEG_QUALITY = 0.95;

/** Catmull-Rom cubic weight (a = -0.5). */
function cubicW(t) {
  const a = -0.5;
  const at = Math.abs(t);
  if (at <= 1) return (a + 2) * at * at * at - (a + 3) * at * at + 1;
  if (at < 2) return a * at * at * at - 5 * a * at * at + 8 * a * at - 4 * a;
  return 0;
}

/**
 * Bicubic sample of RGB at (x, y) written into out[o..o+2].
 * Falls back to white outside the source (same contract as bilinear).
 */
function sampleBicubicInto(data, w, h, x, y, out, o) {
  if (x < 1 || y < 1 || x >= w - 2 || y >= h - 2) {
    if (x < 0 || y < 0 || x >= w - 1 || y >= h - 1) {
      out[o] = 255;
      out[o + 1] = 255;
      out[o + 2] = 255;
      return;
    }
    const v = sampleBilinear(data, w, h, x, y);
    out[o] = v[0];
    out[o + 1] = v[1];
    out[o + 2] = v[2];
    return;
  }
  const x0 = Math.floor(x);
  const y0 = Math.floor(y);
  const fx = x - x0;
  const fy = y - y0;
  const wx0 = cubicW(fx + 1);
  const wx1 = cubicW(fx);
  const wx2 = cubicW(1 - fx);
  const wx3 = cubicW(2 - fx);
  const wy0 = cubicW(fy + 1);
  const wy1 = cubicW(fy);
  const wy2 = cubicW(1 - fy);
  const wy3 = cubicW(2 - fy);
  const wy = [wy0, wy1, wy2, wy3];
  const wx = [wx0, wx1, wx2, wx3];
  let r = 0;
  let g = 0;
  let b = 0;
  for (let j = 0; j < 4; j += 1) {
    const row = (y0 - 1 + j) * w;
    const wj = wy[j];
    for (let i = 0; i < 4; i += 1) {
      const p = (row + x0 - 1 + i) * 4;
      const wgt = wj * wx[i];
      r += data[p] * wgt;
      g += data[p + 1] * wgt;
      b += data[p + 2] * wgt;
    }
  }
  out[o] = r < 0 ? 0 : r > 255 ? 255 : r;
  out[o + 1] = g < 0 ? 0 : g > 255 ? 255 : g;
  out[o + 2] = b < 0 ? 0 : b > 255 ? 255 : b;
}

function sampleBilinear(data, w, h, x, y) {
  if (x < 0 || y < 0 || x >= w - 1 || y >= h - 1) return [255, 255, 255, 255];
  const x0 = Math.floor(x);
  const y0 = Math.floor(y);
  const x1 = x0 + 1;
  const y1 = y0 + 1;
  const fx = x - x0;
  const fy = y - y0;
  const idx = (ix, iy) => (iy * w + ix) * 4;
  const out = [0, 0, 0, 255];
  for (let c = 0; c < 3; c += 1) {
    const v00 = data[idx(x0, y0) + c];
    const v10 = data[idx(x1, y0) + c];
    const v01 = data[idx(x0, y1) + c];
    const v11 = data[idx(x1, y1) + c];
    out[c] = Math.round(
      v00 * (1 - fx) * (1 - fy) +
        v10 * fx * (1 - fy) +
        v01 * (1 - fx) * fy +
        v11 * fx * fy
    );
  }
  return out;
}

export function outputSizeFromCorners(corners, maxSide = WARP_OUTPUT_MAX_SIDE) {
  const [tl, tr, br, bl] = corners;
  let w = Math.max(dist(tl, tr), dist(bl, br));
  let h = Math.max(dist(tl, bl), dist(tr, br));
  w = Math.max(32, Math.round(w));
  h = Math.max(32, Math.round(h));
  const scale = Math.min(1, maxSide / Math.max(w, h));
  return {
    width: Math.max(32, Math.round(w * scale)),
    height: Math.max(32, Math.round(h * scale)),
  };
}

/* ===========================================================================
 * [Fable 5.1 · Upright orientation from text layout]
 * ---------------------------------------------------------------------------
 * Decides how many 90° clockwise turns make a straightened sheet readable.
 * Runs on the warped output (so the sheet is already rectangular). Measured
 * on the 21 labeled invoice warps × 4 rotations (node harness).
 *
 * DESIGN INVARIANTS
 *   1. Ink = GLYPHS only. Dark pixels whose horizontal or vertical run is
 *      longer than 5% of the image are dropped (table rules, logo bars,
 *      barcodes, QR). Those are what made raw-ink margin cues useless.
 *   2. Axis: projection profiles of the glyph mask. Along the line-stacking
 *      axis the profile alternates line/gap (high coefficient of variation);
 *      across it, it is smooth. Rows win → 0/180, columns win → 90/270. On
 *      the fixtures this test was right 84/84 times.
 *   3. Direction, primary cue: invoice text is left-aligned, so line STARTS
 *      align (small robust spread / MAD) and ENDS are ragged. Fires only when
 *      endMAD ≥ dirRatio × startMAD; on upright fixtures it never pointed the
 *      wrong way. Secondary cue, used only when the primary does not fire:
 *      more glyph ink in the top 30% than the bottom 30% (header/logo zone).
 *   4. 90/270 is always applied when the axis test is clear — a sideways
 *      sheet is never readable; a wrong direction costs one Rotate click.
 *      180 is applied only on strong primary evidence (flip180Ratio, ≥
 *      minLines lines): a false flip on an upright invoice is worse than
 *      leaving a rare upside-down one for the user. With the shipped
 *      parameters it fired 0 times on 21 upright fixtures (and 0 times on
 *      their 180° copies) — that asymmetry is intentional.
 *   5. Downsampling is box-averaged, never point-sampled: 1px strokes must
 *      survive and the result must not depend on sample parity (rotation).
 *   6. Returns 0 on any doubt. Never throws. Scorecard: 78/84 on
 *      21 fixtures × 4 rotations, all misses are conservative or one
 *      sideways direction.
 * =========================================================================== */
const UPRIGHT_PARAMS = {
  maxSide: 480,
  inkDrop: 60, // ink = darker than paper (60th percentile luma) by this
  maxRunFrac: 0.05, // longer dark runs are rules/bars, not glyphs
  minInkFrac: 0.003,
  axisRatio: 1.25, // CV(cols) ≥ 1.25 × CV(rows) ⇒ text runs vertically
  lineThr: 0.1, // line = profile ≥ 10% of its max
  minLineFrac: 0.15, // ignore "lines" shorter than 15% of the width
  minLines: 10,
  dirRatio: 2.0,
  flip180Ratio: 2.5,
};

/** Smooth a 1-D profile with a box of half-width r. */
function smooth1d(src, r) {
  const n = src.length;
  const out = new Float32Array(n);
  let sum = 0;
  let cnt = 0;
  for (let i = 0; i < Math.min(n, r); i += 1) {
    sum += src[i];
    cnt += 1;
  }
  for (let i = 0; i < n; i += 1) {
    const add = i + r;
    const drop = i - r - 1;
    if (add < n) {
      sum += src[add];
      cnt += 1;
    }
    if (drop >= 0) {
      sum -= src[drop];
      cnt -= 1;
    }
    out[i] = cnt ? sum / cnt : 0;
  }
  return out;
}

function coefVar(profile) {
  let s = 0;
  for (let i = 0; i < profile.length; i += 1) s += profile[i];
  const mean = s / Math.max(1, profile.length);
  if (mean <= 0) return 0;
  let v = 0;
  for (let i = 0; i < profile.length; i += 1) v += (profile[i] - mean) ** 2;
  return Math.sqrt(v / Math.max(1, profile.length)) / mean;
}

function median(arr) {
  const s = arr.slice().sort((a, b) => a - b);
  return s[Math.floor(s.length / 2)];
}

/** Median absolute deviation — robust spread (Infinity when too few). */
function mad(arr) {
  if (arr.length < 3) return Infinity;
  const m = median(arr);
  return median(arr.map((v) => Math.abs(v - m)));
}

/**
 * Glyph mask (invariant 1) for an already-downsampled gray image.
 */
function glyphInkMask(gray, w, h, p) {
  const sorted = Array.from(gray).sort((a, b) => a - b);
  const paper = sorted[Math.floor(sorted.length * 0.6)];
  const cut = paper - p.inkDrop;
  const dark = new Uint8Array(w * h);
  for (let i = 0; i < w * h; i += 1) dark[i] = gray[i] < cut ? 1 : 0;
  const maxRun = Math.max(8, Math.round(Math.max(w, h) * p.maxRunFrac));
  const runX = new Uint16Array(w * h);
  const runY = new Uint16Array(w * h);
  for (let y = 0; y < h; y += 1) {
    let x = 0;
    while (x < w) {
      if (!dark[y * w + x]) {
        x += 1;
        continue;
      }
      let e = x;
      while (e < w && dark[y * w + e]) e += 1;
      for (let k = x; k < e; k += 1) runX[y * w + k] = e - x;
      x = e;
    }
  }
  for (let x = 0; x < w; x += 1) {
    let y = 0;
    while (y < h) {
      if (!dark[y * w + x]) {
        y += 1;
        continue;
      }
      let e = y;
      while (e < h && dark[e * w + x]) e += 1;
      for (let k = y; k < e; k += 1) runY[k * w + x] = e - y;
      y = e;
    }
  }
  const ink = new Uint8Array(w * h);
  let n = 0;
  for (let i = 0; i < w * h; i += 1) {
    if (dark[i] && runX[i] <= maxRun && runY[i] <= maxRun) {
      ink[i] = 1;
      n += 1;
    }
  }
  return { ink, n };
}

/**
 * Direction cues for HORIZONTAL text in `ink` (w×h). "start" = left side.
 * Returns { lines, startMad, endMad, topMass, botMass }.
 */
function directionCues(ink, w, h, p) {
  const rows = new Float32Array(h);
  for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < w; x += 1) if (ink[y * w + x]) rows[y] += 1;
  }
  let maxR = 0;
  for (let y = 0; y < h; y += 1) if (rows[y] > maxR) maxR = rows[y];
  const thr = maxR * p.lineThr;
  const starts = [];
  const ends = [];
  let y = 0;
  while (y < h) {
    if (rows[y] < thr) {
      y += 1;
      continue;
    }
    let j = y;
    while (j < h && rows[j] >= thr) j += 1;
    let lo = Infinity;
    let hi = -1;
    for (let x = 0; x < w; x += 1) {
      for (let q = y; q < j; q += 1) {
        if (ink[q * w + x]) {
          if (x < lo) lo = x;
          if (x > hi) hi = x;
          break;
        }
      }
    }
    if (hi - lo > w * p.minLineFrac) {
      starts.push(lo);
      ends.push(hi);
    }
    y = j;
  }
  let topMass = 0;
  let botMass = 0;
  for (let yy = 0; yy < h; yy += 1) {
    if (yy < h * 0.3) topMass += rows[yy];
    else if (yy > h * 0.7) botMass += rows[yy];
  }
  return { lines: starts.length, startMad: mad(starts), endMad: mad(ends), topMass, botMass };
}

/** Transpose a w×h mask to h×w (text that ran vertically now runs horizontally). */
function transposeMask(ink, w, h) {
  const out = new Uint8Array(w * h);
  for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < w; x += 1) out[x * h + y] = ink[y * w + x];
  }
  return out;
}

/**
 * [Fable 5.1] Quarter turns (clockwise) that make the sheet upright, or 0.
 * @param {ImageData} imageData warped sheet (any size; downsampled inside)
 */
export function estimateUprightQuarterTurns(imageData, params = UPRIGHT_PARAMS) {
  try {
    const p = { ...UPRIGHT_PARAMS, ...params };
    const W = imageData.width;
    const H = imageData.height;
    const step = Math.max(1, Math.ceil(Math.max(W, H) / p.maxSide));
    const w = Math.floor(W / step);
    const h = Math.floor(H / step);
    if (w < 32 || h < 32) return 0;
    const d = imageData.data;
    // Box-average each step×step block (point sampling drops 1px strokes and
    // makes the result depend on sample parity, i.e. on rotation).
    const gray = new Float32Array(w * h);
    const inv = 1 / (step * step);
    for (let y = 0; y < h; y += 1) {
      for (let x = 0; x < w; x += 1) {
        let s = 0;
        for (let dy = 0; dy < step; dy += 1) {
          let i = ((y * step + dy) * W + x * step) * 4;
          for (let dx = 0; dx < step; dx += 1, i += 4) {
            s += 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
          }
        }
        gray[y * w + x] = s * inv;
      }
    }
    const { ink, n: inkN } = glyphInkMask(gray, w, h, p);
    if (inkN < w * h * p.minInkFrac) return 0;

    // invariant 2: which axis stacks the lines?
    const rows = new Float32Array(h);
    const cols = new Float32Array(w);
    for (let y = 0; y < h; y += 1) {
      for (let x = 0; x < w; x += 1) {
        if (ink[y * w + x]) {
          rows[y] += 1;
          cols[x] += 1;
        }
      }
    }
    const r = Math.max(1, Math.round(Math.min(w, h) / 160));
    const cvRows = coefVar(smooth1d(rows, r));
    const cvCols = coefVar(smooth1d(cols, r));
    const vertical = cvCols >= cvRows * p.axisRatio;
    const horizontal = cvRows >= cvCols * p.axisRatio;
    if (!vertical && !horizontal) return 0;

    if (horizontal) {
      // invariant 4: 180 only on strong primary evidence.
      const c = directionCues(ink, w, h, p);
      if (c.lines >= p.minLines && c.startMad >= c.endMad * p.flip180Ratio) return 2;
      return 0;
    }

    // Vertical text. Rotate the mask 90° CW (transpose + mirror) so that a
    // sheet turned CCW reads normally; then ask the horizontal cues which way.
    // After transpose, original x→y and y→x. A CW-turned sheet (needs 3 CW
    // turns) has its reading-left at the TOP of the warped image ⇒ in the
    // transposed mask, lines start at small x ⇒ "start aligned" ⇒ return 3.
    // A CCW-turned sheet (needs 1 CW) has reading-left at the bottom ⇒ ends
    // aligned in the transposed mask ⇒ return 1.
    const tIn = transposeMask(ink, w, h);
    const c = directionCues(tIn, h, w, p);
    if (c.lines >= p.minLines) {
      if (c.endMad >= c.startMad * p.dirRatio) return 3;
      if (c.startMad >= c.endMad * p.dirRatio) return 1;
    }
    // Secondary cue: header/logo zone has more ink. In the transposed mask the
    // original top edge is at small y when the sheet was turned CCW... use the
    // untransposed geometry directly: original LEFT 30% vs RIGHT 30% columns.
    let leftMass = 0;
    let rightMass = 0;
    for (let x = 0; x < w; x += 1) {
      if (x < w * 0.3) leftMass += cols[x];
      else if (x > w * 0.7) rightMass += cols[x];
    }
    // Header at left ⇒ sheet turned CCW (top went left) ⇒ 1 CW turn fixes it.
    return leftMass >= rightMass ? 1 : 3;
  } catch {
    return 0;
  }
}
/* ================ end [Fable 5.1 · Upright orientation from text layout] == */

/** Rotate an RGBA ImageData by quarter turns clockwise. */
export function rotateImageDataQuarterTurns(imageData, turns) {
  const t = ((turns % 4) + 4) % 4;
  if (t === 0) return imageData;
  const { width: w, height: h, data: src } = imageData;
  const outW = t % 2 ? h : w;
  const outH = t % 2 ? w : h;
  const out = new Uint8ClampedArray(outW * outH * 4);
  for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < w; x += 1) {
      let nx;
      let ny;
      if (t === 1) {
        nx = h - 1 - y;
        ny = x;
      } else if (t === 2) {
        nx = w - 1 - x;
        ny = h - 1 - y;
      } else {
        nx = y;
        ny = w - 1 - x;
      }
      const si = (y * w + x) * 4;
      const di = (ny * outW + nx) * 4;
      out[di] = src[si];
      out[di + 1] = src[si + 1];
      out[di + 2] = src[si + 2];
      out[di + 3] = src[si + 3];
    }
  }
  return { width: outW, height: outH, data: out };
}

/**
 * Warp HTMLImageElement / ImageBitmap with 4 source corners → flat RGBA buffer
 * `{ width, height, data }`. `autoUpright` rotates the result so text reads
 * left→right, top→bottom. Kept separate from encoding so the straighten dialog
 * can re-encode (rotate / enhance toggle) without redoing the warp.
 */
export function warpPerspectiveToImageData(
  image,
  corners,
  { maxSide = WARP_OUTPUT_MAX_SIDE, autoUpright = true } = {}
) {
  const srcW = image.naturalWidth || image.width;
  const srcH = image.naturalHeight || image.height;
  const { width: outW, height: outH } = outputSizeFromCorners(corners, maxSide);
  const dstCorners = [
    { x: 0, y: 0 },
    { x: outW - 1, y: 0 },
    { x: outW - 1, y: outH - 1 },
    { x: 0, y: outH - 1 },
  ];
  const H = destToSrcHomography(corners, dstCorners);
  if (!H) throw new Error("Could not straighten photo");

  const srcCanvas = document.createElement("canvas");
  srcCanvas.width = srcW;
  srcCanvas.height = srcH;
  const sctx = srcCanvas.getContext("2d", { willReadFrequently: true });
  sctx.drawImage(image, 0, 0);
  const srcData = sctx.getImageData(0, 0, srcW, srcH).data;

  const od = new Uint8ClampedArray(outW * outH * 4);
  for (let y = 0; y < outH; y += 1) {
    for (let x = 0; x < outW; x += 1) {
      const p = applyH(H, x, y);
      const i = (y * outW + x) * 4;
      sampleBicubicInto(srcData, srcW, srcH, p.x, p.y, od, i);
      od[i + 3] = 255;
    }
  }
  let warped = { width: outW, height: outH, data: od };
  if (autoUpright) {
    const turns = estimateUprightQuarterTurns(warped);
    if (turns) warped = rotateImageDataQuarterTurns(warped, turns);
  }
  return warped;
}

/** Encode an RGBA `{ width, height, data }` buffer as a JPEG Blob. */
export async function imageDataToJpegBlob(img, quality = WARP_JPEG_QUALITY) {
  const outCanvas = document.createElement("canvas");
  outCanvas.width = img.width;
  outCanvas.height = img.height;
  const octx = outCanvas.getContext("2d");
  const out = octx.createImageData(img.width, img.height);
  out.data.set(img.data);
  octx.putImageData(out, 0, 0);
  return new Promise((resolve, reject) => {
    outCanvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("JPEG encode failed"))),
      "image/jpeg",
      quality
    );
  });
}

/**
 * Warp HTMLImageElement / ImageBitmap with 4 source corners → flat JPEG Blob.
 * `enhance` applies the scan-look filter (white paper, crisp ink) before encode.
 */
export async function warpPerspectiveToJpeg(
  image,
  corners,
  {
    quality = WARP_JPEG_QUALITY,
    maxSide = WARP_OUTPUT_MAX_SIDE,
    autoUpright = true,
    enhance = false,
  } = {}
) {
  let warped = warpPerspectiveToImageData(image, corners, { maxSide, autoUpright });
  if (enhance) {
    const { enhanceDocumentImageData } = await import("./invoiceEnhance");
    warped = enhanceDocumentImageData(warped);
  }
  return imageDataToJpegBlob(warped, quality);
}

export function blobToFile(blob, baseName, { suffix = "straight" } = {}) {
  const stem = String(baseName || "invoice")
    .replace(/\.[^.]+$/, "")
    .replace(/[^\w.-]+/g, "_")
    .slice(0, 80);
  const tag = String(suffix || "out").replace(/[^\w.-]+/g, "") || "out";
  return new File([blob], `${stem || "invoice"}-${tag}.jpg`, {
    type: "image/jpeg",
    lastModified: Date.now(),
  });
}

export function loadImageFromFile(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      resolve({ image: img, objectUrl: url });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not open image"));
    };
    img.src = url;
  });
}

/**
 * Rotate image 90° clockwise (or quarterTurns × 90°). Returns a new JPEG File.
 * Useful when Android camera photos are sideways / upside-down.
 */
export async function rotateImageFile(file, quarterTurns = 1) {
  const turns = ((Number(quarterTurns) % 4) + 4) % 4;
  if (turns === 0) return file;
  const { image, objectUrl } = await loadImageFromFile(file);
  try {
    let w = image.naturalWidth || image.width;
    let h = image.naturalHeight || image.height;
    let src = image;
    let lastCanvas = null;
    for (let i = 0; i < turns; i += 1) {
      const canvas = document.createElement("canvas");
      canvas.width = h;
      canvas.height = w;
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("Could not rotate photo");
      ctx.translate(h, 0);
      ctx.rotate(Math.PI / 2);
      ctx.drawImage(src, 0, 0);
      lastCanvas = canvas;
      src = canvas;
      const nextW = h;
      const nextH = w;
      w = nextW;
      h = nextH;
    }
    const blob = await new Promise((resolve, reject) => {
      lastCanvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error("JPEG encode failed"))),
        "image/jpeg",
        WARP_JPEG_QUALITY
      );
    });
    return blobToFile(blob, file.name, { suffix: "rot" });
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}
